import { Heading, Link } from '@purpur/library';
import styled from 'styled-components';

import { IMAGES_PATH } from '@telialt/assets/src/constants';
import { NamedIcon } from '@telialt/common-icon/src';
import { defaultLinkStyle } from '@telialt/common-theme/src/mixins';
import {
    getPurpurBorder,
    getPurpurColor,
    getPurpurShadow,
    getPurpurSpacing,
    getPurpurTypography,
} from '@telialt/common-theme/src/purpurSelectors';

export const CardWrapper = styled.div`
    display: flex;
    flex-direction: column;
    border-radius: ${getPurpurBorder('radiusLg')};
    border: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('borderWeak')};
    background-color: ${getPurpurColor('functionalWhite')};
    overflow: hidden;
    text-decoration: none;
    position: relative;
    height: 100%;

    &:focus-within {
        outline: 2px solid ${getPurpurColor('purple500')};
        outline-offset: 2px;
    }
    &:focus-within:not(:has(:focus-visible)) {
        outline: 0;
    }

    &:hover {
        box-shadow: ${getPurpurShadow('md')};
    }
`;

export const ClickableArea = styled.a`
    ${defaultLinkStyle()};
    display: flex;
    flex-direction: column;
    flex: 1;
    text-decoration: none;

    &:active,
    &:hover,
    &:focus {
        color: ${getPurpurColor('textDefault')};
        text-decoration: none;
        outline: none;
    }
`;

export const BadgeWrapper = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${getPurpurSpacing('100')};
    flex-wrap: wrap;
    margin: 0 ${getPurpurSpacing('200')} ${getPurpurSpacing('100')};
    height: 1.375rem;
`;

export const ImageWrapper = styled.div`
    position: relative;
    overflow: hidden;
    display: flex;
    justify-content: center;
    align-items: center;
    margin: 0 ${getPurpurSpacing('200')};
    height: 13.75rem;
`;

export const StockStatus = styled.div`
    font-size: ${getPurpurTypography('scale50')};
    color: ${getPurpurColor('textStatusErrorMedium')};
    margin-top: ${getPurpurSpacing('100')};
    padding: 0 ${getPurpurSpacing('200')};
    height: 1.125rem;
`;

export const MarketingTag = styled.img`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 0;
    width: auto;
    height: auto;
    pointer-events: none;
    max-width: 5rem;
    max-height: 5rem;
`;

export const StyledImage = styled.img`
    width: 100%;
    height: 100%;
    object-fit: contain;
`;

export const StyledHeading = styled(Heading)`
    padding: 0 ${getPurpurSpacing('200')};
    margin-top: 0;
    word-break: break-word;
    height: 3.125rem;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

export const ColorDotsWrapper = styled.div`
    display: flex;
    flex-direction: row;
    gap: ${getPurpurSpacing('50')};
    flex-wrap: wrap;
    margin: ${getPurpurSpacing('100')} ${getPurpurSpacing('200')} 0 ${getPurpurSpacing('200')};
    min-height: 0.75rem;
`;

const CustomColorDot = styled.div`
    border-radius: ${getPurpurBorder('radiusFull')};
    height: 0.75rem;
    width: 0.75rem;
    background-color: white;
    background-position: 80% 50%;
    background-size: 120%;
`;

export const TransparentColorDot = styled(CustomColorDot)`
    background-image: url('${IMAGES_PATH}/temp/transparent-color-badge.svg');
`;

export const MultiColorDot = styled(CustomColorDot)`
    background-image: url('${IMAGES_PATH}/temp/multicolor-color-badge.svg');
`;

export const InfoWrapper = styled.div`
    display: flex;
    flex-direction: column;
    background-color: ${getPurpurColor('gray50')};
    border-top: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray100')};
    border-bottom: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray100')};
    padding: ${getPurpurSpacing('200')};
    margin-top: ${getPurpurSpacing('200')};
    flex: 1;
`;

export const PriceBlock = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    line-height: 1.25rem;
`;

export const PriceBlockContent = styled.div`
    flex-grow: 1;
`;

export const LeasingInitialAmountBlock = styled.div`
    display: flex;
    flex-direction: column;
`;

type FullPriceBlockProps = {
    $hasMonthlyPrice: boolean;
};

export const FullPriceBlock = styled.div<FullPriceBlockProps>`
    border-left: ${({ $hasMonthlyPrice }) =>
        $hasMonthlyPrice ? `${getPurpurBorder('widthXs')} solid #d9d9d9` : ' none'};
    padding-left: ${({ $hasMonthlyPrice }) => ($hasMonthlyPrice ? getPurpurSpacing('200') : 0)};
    margin-left: ${({ $hasMonthlyPrice }) => ($hasMonthlyPrice ? getPurpurSpacing('200') : 0)};
    display: flex;
    flex-direction: column;
`;

export const GrayText = styled.span`
    font-size: ${getPurpurTypography('scale50')};
    color: ${getPurpurColor('textMedium')};
`;

export const MonthlyPriceLabelText = styled.span`
    font-size: ${getPurpurTypography('scale50')};
    color: ${getPurpurColor('textMedium')};
    margin-right: ${getPurpurSpacing('50')};
`;

export const PriceText = styled.span`
    font-size: ${getPurpurTypography('scale200')};
    font-weight: ${getPurpurTypography('weightBold')};
    color: ${getPurpurColor('textDefault')};
`;

export const CurrencyTextBold = styled.span`
    font-size: ${getPurpurTypography('scale75')};
    font-weight: ${getPurpurTypography('weightBold')};
    margin-left: ${getPurpurSpacing('50')};
`;

export const OldPrice = styled.div`
    font-size: ${getPurpurTypography('scale50')};
    color: ${getPurpurColor('textDefault')};
    text-decoration: line-through;
`;

export const PlanCommitmentBlock = styled.div`
    font-size: ${getPurpurTypography('scale50')};
    color: ${getPurpurColor('textMedium')};
    margin-top: ${getPurpurSpacing('100')};
`;

export const BenefitsBlock = styled.div`
    display: flex;
    flex-direction: column;
    gap: ${getPurpurSpacing('100')};
    padding: ${getPurpurSpacing('200')} ${getPurpurSpacing('200')};
    border-bottom: ${getPurpurBorder('widthXs')} solid ${getPurpurColor('gray100')};
`;

export const BenefitItem = styled.div`
    display: flex;
    align-items: center;
    gap: ${getPurpurSpacing('100')};
`;

export const BenefitIcon = styled(NamedIcon)`
    flex-shrink: 0;
    color: ${getPurpurColor('brandCorePurple')};
`;

export const BenefitLabel = styled.span`
    font-weight: ${getPurpurTypography('weightMedium')};
    line-height: ${getPurpurTypography('lineHeightLoose')};
    font-size: ${getPurpurTypography('scale75')};
`;

export const CheckboxWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
`;

export const SkipToCompareLink = styled(Link)`
    position: absolute;
    left: -10000px;
    top: auto;
    width: 1px;
    height: 1px;
    overflow: hidden;
    margin-left: ${getPurpurSpacing('200')};

    &:focus-within {
        position: static;
        width: auto;
        height: auto;
        overflow: visible;
    }
`;
